// frontend/src/components/ProjectList.js

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  IconButton,
  Divider,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  QuestionAnswer as QuestionIcon,
  Assessment as ResponseIcon,
  CalendarToday as DateIcon,
} from '@mui/icons-material';
import api from '../services/api';

export function ProjectList() {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchProjects = () => {
    setIsLoading(true);
    api.get('/projects')
      .then(response => {
        if (Array.isArray(response.data)) {
          setProjects(response.data);
        } else {
          setError('Invalid data format received from the server');
        }
      })
      .catch(error => {
        console.error('API error:', error);
        setError('Failed to fetch projects');
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleDelete = (projectId, event) => {
    event.stopPropagation();
    if (window.confirm('Are you sure you want to delete this project?')) {
      api.delete(`/projects/${projectId}`)
        .then(() => {
          setProjects(projects.filter(p => p.id !== projectId));
        })
        .catch(error => {
          console.error('Failed to delete project:', error);
          setError('Failed to delete project. Please try again.');
        });
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, bgcolor: 'rgb(250, 250, 250)', minHeight: '100vh' }}>
      <Paper elevation={0} sx={{ height: '100%' }}>
        <Box sx={{ 
          p: 3,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <Box sx={{ 
            p: 2,
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'grey.200'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              Projects
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/dashboard/projects/create')}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              Create New Project
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
              {error}
            </Alert>
          )}

          {projects.length > 0 ? (
            <List disablePadding>
              {projects.map((project, index) => (
                <React.Fragment key={project.id}>
                  <ListItem
                    sx={{
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'grey.50'
                      }
                    }}
                    onClick={() => navigate(`/dashboard/projects/${project.id}`)}
                  >
                    <ListItemText
                      primary={
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 500,
                            color: 'text.primary',
                            mb: 0.5
                          }}
                        >
                          {project.name}
                        </Typography>
                      }
                      secondary={
                        <Stack 
                          direction="row" 
                          spacing={2} 
                          sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.875rem'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <DateIcon sx={{ fontSize: 16, mr: 0.5, opacity: 0.7 }} />
                            {new Date(project.created_at).toLocaleDateString()}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <QuestionIcon sx={{ fontSize: 16, mr: 0.5, opacity: 0.7 }} />
                            {project.conversation_guide_question_count} questions
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ResponseIcon sx={{ fontSize: 16, mr: 0.5, opacity: 0.7 }} />
                            {project.survey_completion_count} responses
                          </Box>
                        </Stack>
                      }
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => handleDelete(project.id, e)}
                      sx={{
                        color: 'grey.400',
                        '&:hover': {
                          color: 'error.main',
                          bgcolor: 'error.lighter'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                  {index < projects.length - 1 && (
                    <Divider sx={{ ml: 2, mr: 2 }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ 
              py: 8, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center' 
            }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                No projects found
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => navigate('/dashboard/projects/create')}
              >
                Create your first project
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}